// src/pages/Admin/AdminAlerts/AdminAlerts.jsx
import React, { useState, useEffect, useContext, useCallback } from 'react';
import AdminSidebar from '../../../components/AdminSidebar/AdminSidebar';
import { AuthContext } from '../../../context/AuthContext';
import { useToast } from '../../../hooks/useToast';
import './AdminAlerts.css';

// URL base de la API
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://riasbaixaspd.es/api';

const AdminAlerts = () => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAlert, setEditingAlert] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    type: 'info',
    isActive: true,
    expiresAt: '',
    showOnPages: 'all',
    position: 'top-center',
    isFixed: false
  });

  const { authToken } = useContext(AuthContext);
  const { addToast } = useToast();

  // Función para cargar alertas con useCallback
  const fetchAlerts = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/alerts`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAlerts(data);
      } else {
        throw new Error('Error al cargar alertas');
      }
    } catch (err) {
      setError(err.message);
      addToast('Error al cargar las alertas', 'error');
    } finally {
      setLoading(false);
    }
  }, [authToken, addToast]);

  // Cargar alertas al montar el componente
  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editingAlert 
        ? `${API_BASE_URL}/alerts/${editingAlert.id}`
        : `${API_BASE_URL}/alerts`;
      
      const method = editingAlert ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        addToast(
          editingAlert 
            ? 'Alerta actualizada correctamente' 
            : 'Alerta creada correctamente', 
          'success'
        );
        fetchAlerts();
        closeModal();
      } else {
        throw new Error('Error al guardar la alerta');
      }
    } catch (err) {
      addToast('Error al guardar la alerta', 'error');
    }
  };

  const handleEdit = (alert) => {
    setEditingAlert(alert);
    setFormData({
      title: alert.title,
      message: alert.message,
      type: alert.type,
      isActive: alert.is_active,
      expiresAt: alert.expires_at ? alert.expires_at.split('T')[0] : '',
      showOnPages: alert.show_on_pages,
      position: alert.position || 'top-center',
      isFixed: alert.is_fixed || false
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar esta alerta?')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/alerts/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        addToast('Alerta eliminada correctamente', 'success');
        fetchAlerts();
      } else {
        throw new Error('Error al eliminar la alerta');
      }
    } catch (err) {
      addToast('Error al eliminar la alerta', 'error');
    }
  };

  const toggleActive = async (id, currentStatus) => {
    try {
      const response = await fetch(`${API_BASE_URL}/alerts/${id}/toggle`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ isActive: !currentStatus })
      });

      if (response.ok) {
        addToast(
          !currentStatus ? 'Alerta activada' : 'Alerta desactivada', 
          'success'
        );
        fetchAlerts();
      } else {
        throw new Error('Error al cambiar el estado de la alerta');
      }
    } catch (err) {
      addToast('Error al cambiar el estado de la alerta', 'error');
    }
  };

  const openModal = () => {
    setEditingAlert(null);
    setFormData({
      title: '',
      message: '',
      type: 'info',
      isActive: true,
      expiresAt: '',
      showOnPages: 'all',
      position: 'top-center',
      isFixed: false
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingAlert(null);
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'warning': return '⚠️';
      case 'error': return '❌';
      case 'success': return '✅';
      case 'info': return 'ℹ️';
      default: return 'ℹ️';
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'warning': return 'Advertencia';
      case 'error': return 'Error';
      case 'success': return 'Éxito';
      case 'info': return 'Información';
      default: return 'Información';
    }
  };

  if (loading) {
    return (
      <div className="admin-layout">
        <AdminSidebar />
        <div className="admin-main-content">
          <div className="admin-alerts-loading">
            <div className="spinner"></div>
            <p>Cargando alertas...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-alerts-container">
      <AdminSidebar />
      <div className="admin-content">
        <h2 className="admin-page-title">🔔 Gestión de Alertas</h2>
        <p className="admin-subtitle">Administra las notificaciones y alertas del sistema</p>

        {/* Mostrar errores */}
        {error && (
          <div className="alert alert-error">
            <i className="icon-error"></i>
            {error}
          </div>
        )}

        {/* Panel principal de alertas */}
        <div className="alerts-panel">
          <div className="panel-header">
            <h3 className="panel-title">🔔 Sistema de Alertas y Notificaciones</h3>
            <p className="panel-subtitle">Crea y gestiona alertas personalizadas para diferentes páginas del sitio</p>
          </div>

          <div className="alerts-controls">
            <button onClick={openModal} className="alerts-add-btn">
              <span className="btn-icon">+</span>
              Nueva Alerta
            </button>
          </div>

          <div className="alerts-content">
            <div className="alerts-stats">
              <div className="stat-card">
                <div className="stat-icon">📊</div>
                <div className="stat-info">
                  <h4>Total Alertas</h4>
                  <p>{alerts.length}</p>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">✅</div>
                <div className="stat-info">
                  <h4>Activas</h4>
                  <p>{alerts.filter(a => a.is_active).length}</p>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">⏸️</div>
                <div className="stat-info">
                  <h4>Inactivas</h4>
                  <p>{alerts.filter(a => !a.is_active).length}</p>
                </div>
              </div>
            </div>

            <div className="alerts-grid">
            {alerts.length === 0 ? (
              <div className="no-alerts">
                <h3>No hay alertas configuradas</h3>
                <p>Crea tu primera alerta para comenzar</p>
              </div>
            ) : (
              alerts.map(alert => (
                <div key={alert.id} className={`alert-card ${alert.is_active ? 'active' : 'inactive'}`}>
                  <div className="alert-card-header">
                    <div className="alert-type">
                      <span className="alert-icon">{getTypeIcon(alert.type)}</span>
                      <span className="alert-type-label">{getTypeLabel(alert.type)}</span>
                    </div>
                    <div className="alert-status">
                      <span className={`status-badge ${alert.is_active ? 'active' : 'inactive'}`}>
                        {alert.is_active ? 'Activa' : 'Inactiva'}
                      </span>
                    </div>
                  </div>
                  
                  <div className="alert-card-content">
                    <h3>{alert.title}</h3>
                    <p>{alert.message}</p>
                    
                    <div className="alert-meta">
                      <small>
                        Páginas: {alert.show_on_pages === 'all' ? 'Todas las páginas' : 
                                  alert.show_on_pages === 'home' ? 'Solo página principal' : 
                                  'Solo panel admin'}
                      </small>
                      <small>
                        Posición: {alert.position ? 
                          alert.position.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase()) : 
                          'Arriba Centro'}
                      </small>
                      {alert.is_fixed && (
                        <small className="fixed-badge">
                          📌 Alerta fija
                        </small>
                      )}
                      {alert.expires_at && (
                        <small>
                          Expira: {new Date(alert.expires_at).toLocaleDateString('es-ES', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </small>
                      )}
                      <small>
                        Creada: {new Date(alert.created_at).toLocaleDateString('es-ES', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </small>
                    </div>
                  </div>
                  
                  <div className="alert-card-actions">
                    <button 
                      onClick={() => toggleActive(alert.id, alert.is_active)}
                      className={`btn btn-sm ${alert.is_active ? 'btn-warning' : 'btn-success'}`}
                    >
                      {alert.is_active ? 'Desactivar' : 'Activar'}
                    </button>
                    <button 
                      onClick={() => handleEdit(alert)}
                      className="btn btn-sm btn-secondary"
                    >
                      Editar
                    </button>
                    <button 
                      onClick={() => handleDelete(alert.id)}
                      className="btn btn-sm btn-danger"
                    >
                      Eliminar
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Modal para crear/editar alertas */}
          {isModalOpen && (
            <div className="modal-overlay">
              <div className="modal-content">
                <div className="modal-header">
                  <h2>{editingAlert ? 'Editar Alerta' : 'Nueva Alerta'}</h2>
                  <button onClick={closeModal} className="modal-close">×</button>
                </div>
                
                <form onSubmit={handleSubmit} className="alert-form">
                  <div className="form-group">
                    <label htmlFor="title">Título:</label>
                    <input
                      type="text"
                      id="title"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      required
                      placeholder="Título de la alerta"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="message">Mensaje:</label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows="4"
                      placeholder="Contenido de la alerta"
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="type">Tipo:</label>
                      <select
                        id="type"
                        name="type"
                        value={formData.type}
                        onChange={handleInputChange}
                      >
                        <option value="info">Información</option>
                        <option value="warning">Advertencia</option>
                        <option value="error">Error</option>
                        <option value="success">Éxito</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label htmlFor="showOnPages">Mostrar en:</label>
                      <select
                        id="showOnPages"
                        name="showOnPages"
                        value={formData.showOnPages}
                        onChange={handleInputChange}
                      >
                        <option value="all">Todas las páginas</option>
                        <option value="home">Solo página principal</option>
                        <option value="admin">Solo panel admin</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="position">Posición en pantalla:</label>
                      <select
                        id="position"
                        name="position"
                        value={formData.position}
                        onChange={handleInputChange}
                      >
                        <option value="top-center">Arriba Centro</option>
                        <option value="top-left">Arriba Izquierda</option>
                        <option value="top-right">Arriba Derecha</option>
                        <option value="bottom-center">Abajo Centro</option>
                        <option value="bottom-left">Abajo Izquierda</option>
                        <option value="bottom-right">Abajo Derecha</option>
                        <option value="center">Centro de Pantalla</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label htmlFor="expiresAt">Fecha de expiración (opcional):</label>
                      <input
                        type="date"
                        id="expiresAt"
                        name="expiresAt"
                        value={formData.expiresAt}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          name="isActive"
                          checked={formData.isActive}
                          onChange={handleInputChange}
                        />
                        Activar alerta inmediatamente
                      </label>
                    </div>

                    <div className="form-group">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          name="isFixed"
                          checked={formData.isFixed}
                          onChange={handleInputChange}
                        />
                        Alerta fija (no se puede cerrar)
                      </label>
                    </div>
                  </div>

                  <div className="form-actions">
                    <button type="button" onClick={closeModal} className="btn btn-secondary">
                      Cancelar
                    </button>
                    <button type="submit" className="btn btn-primary">
                      {editingAlert ? 'Actualizar' : 'Crear'} Alerta
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAlerts;